import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { Check, Clock, X } from "lucide-react";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseBadge from "@/components_v2/Badge/BaseBadge";
import {
  ApplicationStatuses,
  OrganizationRoles,
} from "@/shared/utils/constants";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import ApplicationAcceptModal from "@/features/organizations/profile/components/ApplicationAcceptModal";
import ApplicationRejectModal from "@/features/organizations/profile/components/ApplicationRejectModal";
import {
  ApplicationDetailFieldProps,
  ApplicationDetailModalProps,
} from "@/features/organizations/profile/types";

import "../styles.css";

const ApplicationDetailField = ({
  label,
  children,
  className,
}: ApplicationDetailFieldProps) => (
  <div
    className={`application-detail-modal__field flex flex-col ${className ?? ""}`}
  >
    <span className="application-detail-modal__label body-s-regular">
      {label}
    </span>
    <div className="application-detail-modal__value body-m-regular">
      {children}
    </div>
  </div>
);

const ApplicationDetailModal = ({
  open,
  setOpen,
  application,
  onUpdated,
}: ApplicationDetailModalProps) => {
  const { t } = useTranslation();

  const [openAcceptModal, setOpenAcceptModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);

  const onClose = () => setOpen(false);

  const status = ApplicationStatuses.getItemByValue(application.status);
  const isPending = application.status === ApplicationStatuses.PENDING.value;

  return (
    <>
      <BaseModal
        destroyOnClose
        className="application-detail-modal"
        title={t("organizations.labels.applicationDetails")}
        open={open}
        onCancel={onClose}
        footer={
          <div className="modal-footer-buttons">
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.TERTIARY}
              onClick={onClose}
            >
              {t("organizations.labels.close")}
            </BaseButton>

            {isPending && (
              <div className="flex gap-200">
                <BaseButton
                  variant={BUTTON_VARIANTS.DANGER}
                  size={BUTTON_SIZES.SMALL}
                  leadingIcon={<X size={16} />}
                  onClick={() => setOpenRejectModal(true)}
                >
                  {t("organizations.labels.rejectApplication")}
                </BaseButton>

                <BaseButton
                  variant={BUTTON_VARIANTS.PRIMARY}
                  size={BUTTON_SIZES.SMALL}
                  leadingIcon={<Check size={16} />}
                  onClick={() => setOpenAcceptModal(true)}
                >
                  {t("organizations.labels.acceptApplication")}
                </BaseButton>
              </div>
            )}
          </div>
        }
      >
        <div className="flex flex-col gap-400">
          <div className="application-detail-modal__grid">
            <ApplicationDetailField label={t("organizations.labels.firstName")}>
              {application.firstName}
            </ApplicationDetailField>

            <ApplicationDetailField label={t("organizations.labels.lastName")}>
              {application.lastName}
            </ApplicationDetailField>

            <ApplicationDetailField label={t("organizations.labels.email")}>
              {application.email}
            </ApplicationDetailField>

            <ApplicationDetailField
              label={t("organizations.labels.appliedDate")}
            >
              {DateTime.fromISO(application.createdAt).toFormat("dd MMMM yyyy")}
            </ApplicationDetailField>

            <ApplicationDetailField label={t("organizations.labels.status")}>
              <BaseBadge
                leadingIcon={isPending ? <Clock size={14} /> : undefined}
                backgroundColor={status?.backgroundColor}
                fontColor={status?.color}
                borderColor={status?.color}
              >
                {status?.label || application.status}
              </BaseBadge>
            </ApplicationDetailField>
          </div>

          {application.note && (
            <ApplicationDetailField
              label={t("organizations.labels.note")}
              className="application-detail-modal__field--full"
            >
              {application.note}
            </ApplicationDetailField>
          )}

          {application.status === ApplicationStatuses.REJECTED.value &&
            application.rejectionReason && (
              <ApplicationDetailField
                label={t("organizations.labels.rejectionReason")}
                className="application-detail-modal__field--full"
              >
                {application.rejectionReason}
              </ApplicationDetailField>
            )}

          {(application.status === ApplicationStatuses.ACCEPTED.value ||
            application.status === ApplicationStatuses.REGISTERED.value) && (
            <ApplicationDetailField
              label={t("organizations.labels.assignedRole")}
              className="application-detail-modal__field--full"
            >
              {OrganizationRoles.getItemByValue(application.assignedRole)
                ?.label || application.assignedRole}
            </ApplicationDetailField>
          )}
        </div>
      </BaseModal>

      <ApplicationAcceptModal
        open={openAcceptModal}
        setOpen={setOpenAcceptModal}
        application={application}
        onAccepted={() => {
          onClose();
          onUpdated();
        }}
      />

      <ApplicationRejectModal
        open={openRejectModal}
        setOpen={setOpenRejectModal}
        application={application}
        onRejected={() => {
          onClose();
          onUpdated();
        }}
      />
    </>
  );
};

export default ApplicationDetailModal;
